/**
 * configFilters.js — per-event-type availability for admin-config vocabularies.
 *
 * Each treatmentType / severityCode / statusValue item MAY carry an
 * `eventTypes: string[]` array listing the event types it applies to. The rule:
 *   - absent or empty  → available for ALL event types (backward compatible)
 *   - otherwise        → available only for the listed event types
 *
 * This is what makes the admin config actually drive the new-case form, instead
 * of being cosmetic naming.
 */

// Canonical event-type values. (Phase 2 makes these admin-configurable; the
// values stay a fixed enum aligned with the API/DB `event_type` column.)
export const EVENT_TYPE_VALUES = ['Treatment', 'NearMiss', 'ClinicIncident'];

/** Is a single config item available for the given event type? */
export function itemAvailableForEventType(item, eventType) {
  if (!eventType) return true;                 // no event type chosen yet → show all
  const types = item?.eventTypes;
  if (!Array.isArray(types) || types.length === 0) return true; // unset → all
  return types.includes(eventType);
}

/**
 * Filter a vocabulary list by event type.
 * @param {Array} items
 * @param {string} eventType
 * @param {{ includeValue?: string }} [opts] — always keep the item whose
 *        `value` equals includeValue, even if not available. Used so an
 *        already-saved case can still display/keep its current selection
 *        ("grandfathering") even after an admin restricts that value.
 */
export function filterByEventType(items, eventType, { includeValue } = {}) {
  return (items || []).filter(item =>
    itemAvailableForEventType(item, eventType) ||
    (includeValue != null && item?.value === includeValue),
  );
}

// ── One level down: per-TREATMENT-type availability (LHR, AWI, …) ────────────

/**
 * Canonical treatment `value` for a case's stored treatment/department string.
 * Cases may carry an alias ('LASER', 'BOTOX' — SP-migrated legacy) rather than
 * the canonical value; match aliases first, exactly like TreatmentBadge does.
 */
export function resolveTreatmentValue(treatmentTypes, raw) {
  if (!raw) return null;
  const upper = String(raw).toUpperCase();
  const entry = (treatmentTypes || []).find(t =>
    (t.aliases || []).some(a => upper === a.toUpperCase() || upper.includes(a.toUpperCase()))
  ) || (treatmentTypes || []).find(t => t.value === raw);
  return entry?.value || raw;
}

/**
 * Is a config item (e.g. a status) available for the given case treatment?
 *   - item.treatmentTypes absent/empty → available for ALL treatments
 *   - restricted + case has NO treatment (clinic incident) → NOT available
 *     (deliberately different from the event-type helper: an LHR-only status
 *     makes no sense on a case with no treatment at all)
 * @param {object} item            config entry carrying treatmentTypes: string[]
 * @param {string} treatment       the case's treatment/department string
 * @param {Array}  treatmentTypes  the config treatmentTypes vocabulary (for alias resolution)
 */
export function itemAvailableForTreatmentType(item, treatment, treatmentTypes) {
  const types = item?.treatmentTypes;
  if (!Array.isArray(types) || types.length === 0) return true; // unset → all
  if (!treatment) return false;
  return types.includes(resolveTreatmentValue(treatmentTypes, treatment));
}
